import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  Box,
  Checkbox,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { FlexCenterWrapper } from "@/components/DefaultNoDataWrapper";
import Flex from "@/components/Flex";
import LoadingOverlay from "@/components/LoadingOverlay";
import StyledButtonV2 from "@/components/app/StyledButtonV2";
import useCurrentUser from "@/hooks/local-storage/use-current-user";
import useSelectedPatient from "@/hooks/local-storage/use-selected-patient";
import { useSnackbar } from "@/hooks/useSnackbar";
import { useScreeningList } from "@/queries/encounter/screening/use-screening-get-list";
import { useScreeningUpdate } from "@/queries/encounter/screening/use-screening-update";
import {
  DEF_PAGINATION,
  DEFAULT_FONTSIZE,
  getXtenantId,
  showToastOnApiCall,
} from "@/utils/utils";
import FreeTextScreeningForm from "../Assessment/FreeTextScreeningForm";
import {
  HOME_HEALTH_SCREENER_TYPES,
  TitleMapper,
} from "../Assessment/assessment-constant";
import { useFreeTextScreener } from "../Assessment/hooks/use-free-text-screener";

// Home Care screeners offered in the New Screening Assessment drawer. Each one
// is a screener type with its questions seeded from HomeCareScreenerQuestion.csv.
export const HOME_CARE_SCREENERS = [
  {
    value: HOME_HEALTH_SCREENER_TYPES.annualWellness,
    label: "Annual Wellness",
  },
  { value: HOME_HEALTH_SCREENER_TYPES.diabetic, label: "Diabetic Screening" },
  { value: HOME_HEALTH_SCREENER_TYPES.smoking, label: "Smoking Screening" },
  { value: HOME_HEALTH_SCREENER_TYPES.obesity, label: "Obesity Screening" },
];

interface HomeCareScreeningLayoutProps {
  onBack: () => void;
  onClose: () => void;
}

const HomeCareScreeningLayout = ({
  onBack,
  onClose,
}: HomeCareScreeningLayoutProps) => {
  const [selected, setSelected] = useState<string | null>(null);
  const [selectedPatient] = useSelectedPatient();
  const [me] = useCurrentUser();
  const showSnackbar = useSnackbar();
  const queryClient = useQueryClient();
  const { mutateAsync: updateScreeningAns } = useScreeningUpdate();

  const { data, isLoading } = useScreeningList({
    pagination: DEF_PAGINATION,
    gridSorting: [],
    type: selected ?? "",
    xTENANTID: getXtenantId(),
  });
  const freeText = useFreeTextScreener(
    selected,
    data?.data?.screeners,
    undefined,
    undefined,
    data?.data?.answerOptions
  );

  const finish = () => {
    if (!selected || !selectedPatient?.uuid) return;
    if (freeText.isEmpty || freeText.issue) {
      showSnackbar({
        message: freeText.issue || "Please answer at least one question.",
        type: "error",
      });
      return;
    }
    showToastOnApiCall({
      apiCall: updateScreeningAns({
        patientUuid: selectedPatient.uuid,
        xTENANTID: getXtenantId(),
        patientScreener: {
          screenerQuestion: freeText.toRows() as any,
          // Patient-level screening from the chart, no appointment.
          appointmentId: undefined as any,
          screenerType: selected as any,
          submittedBy: "PROVIDER",
          providerName: me?.firstName + " " + me?.lastName,
          providerRole: me?.role,
        },
      }),
      showSnackbar,
      onSave: () => {
        queryClient.invalidateQueries({ queryKey: ["all-screening-list"] });
        queryClient.invalidateQueries({
          queryKey: ["graph-assessment-filled"],
        });
        queryClient.invalidateQueries({ queryKey: ["graph-assessment"] });
        onClose();
      },
    });
  };

  return (
    <Flex gap={1} width={"100%"} height={"100%"}>
      <Box
        width={"24%"}
        sx={{
          borderRight: "1px solid #e0e0e0",
          backgroundColor: "#fafafa",
          height: "100%",
        }}
      >
        <Box sx={{ borderBottom: "1px solid #e0e0e0", pb: 1 }}>
          <Flex alignItems="center" gap={1}>
            <IconButton onClick={onBack}>
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            <Typography fontWeight="bold">Choose a Screening</Typography>
          </Flex>
          <Typography variant="subtitle1" sx={{ fontWeight: "medium" }} ml={2}>
            Home Care Assessments
          </Typography>
        </Box>
        <List sx={{ p: 0 }}>
          {HOME_CARE_SCREENERS.map((screener) => (
            <ListItem key={screener.value} disablePadding>
              <ListItemButton
                onClick={() => setSelected(screener.value)}
                sx={{
                  bgcolor:
                    selected === screener.value
                      ? "primary.light"
                      : "transparent",
                  py: 1.5,
                }}
              >
                <Checkbox checked={selected === screener.value} />
                <ListItemText
                  primary={screener.label}
                  primaryTypographyProps={{
                    fontSize: DEFAULT_FONTSIZE.fontSize,
                    color:
                      selected === screener.value ? "primary.main" : "inherit",
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>

      <Flex flexDirection={"column"} gap={1} width={"76%"} height={"100%"}>
        <LoadingOverlay isLoading={isLoading} />
        {!selected || !freeText.def ? (
          <Flex width={"100%"} height={"90%"}>
            <FlexCenterWrapper>Please Select a Screening</FlexCenterWrapper>
          </Flex>
        ) : (
          <>
            <Box sx={{ overflow: "auto", flex: 1, pr: 1 }}>
              <FreeTextScreeningForm
                def={freeText.def}
                questions={data?.data?.screeners ?? []}
                values={freeText.values}
                onChange={freeText.setValue}
                titleDetail={TitleMapper[selected]}
              />
            </Box>
            <Flex justifyContent={"end"} mr={1}>
              <StyledButtonV2
                label={"Finish"}
                disabled={freeText.isEmpty || !!freeText.issue}
                onClick={finish}
              />
            </Flex>
          </>
        )}
      </Flex>
    </Flex>
  );
};

export default HomeCareScreeningLayout;
